import { MilestoneMap1, Objective1 } from '../../types';
import { DependencyRecord } from '@mm-shared/dependencies';
import { DataNode } from './types';
import { buildKeyDetailsDataNode as buildKeyDetailsDataNodeImpl } from './specific-builders/keyDetailsNodeBuilder';
import { buildObjectivesDataNode as buildObjectivesDataNodeImpl } from './specific-builders/objectivesNodeBuilder';
import { buildDatesDataNode as buildDatesDataNodeImpl } from './specific-builders/datesNodeBuilder';

/**
 * Build Key Details node structure
 *
 * Structure: Milestone Map → Major Objective → Objective → Key Details
 * (Key Details include: Milestones, owners, plans & tasks, additional information)
 */
export function buildKeyDetailsDataNode(
  milestoneMap: MilestoneMap1,
  objectives: Objective1[],
  dependencyRecords: DependencyRecord[] = [],
  allMaps: MilestoneMap1[] = [],
): DataNode<MilestoneMap1> {
  try {
    return buildKeyDetailsDataNodeImpl(milestoneMap, objectives, dependencyRecords, allMaps);
  } catch (error) {
    console.error('Error building Key Details data node:', error);
    throw error;
  }
}

/**
 * Build Objectives node structure
 *
 * Structure: Milestone Map → Major Objective → Objective → Reporting Date → Status Report
 * (Recommended for reviewing status objective by objective)
 */
export function buildObjectivesDataNode(
  milestoneMap: MilestoneMap1,
  objectives: Objective1[],
  dependencyRecords: DependencyRecord[] = [],
  allMaps: MilestoneMap1[] = [],
): DataNode<MilestoneMap1> {
  try {
    return buildObjectivesDataNodeImpl(milestoneMap, objectives, dependencyRecords, allMaps);
  } catch (error) {
    console.error('Error building Objectives data node:', error);
    throw error;
  }
}

/**
 * Build Dates node structure
 *
 * Structure: Milestone Map → Reporting Date → Objective → Status Report
 * (Recommended for reviewing status of all objectives for one time interval)
 */
export function buildDatesDataNode(
  milestoneMap: MilestoneMap1,
  objectives: Objective1[],
  dependencyRecords: DependencyRecord[] = [],
  allMaps: MilestoneMap1[] = [],
): DataNode<MilestoneMap1> {
  try {
    return buildDatesDataNodeImpl(milestoneMap, objectives, dependencyRecords, allMaps);
  } catch (error) {
    console.error('Error building Dates data node:', error);
    throw error;
  }
}

/**
 * Build all three node types at once
 *
 * Convenience function for getting all node variations
 */
export function buildAllDataNodes(
  milestoneMap: MilestoneMap1,
  objectives: Objective1[],
  dependencyRecords: DependencyRecord[] = [],
  allMaps: MilestoneMap1[] = [],
): {
  keyDetailsDataNode: DataNode<MilestoneMap1>;
  objectivesDataNode: DataNode<MilestoneMap1>;
  datesDataNode: DataNode<MilestoneMap1>;
} {
  return {
    keyDetailsDataNode: buildKeyDetailsDataNode(
      milestoneMap,
      objectives,
      dependencyRecords,
      allMaps,
    ),
    objectivesDataNode: buildObjectivesDataNode(
      milestoneMap,
      objectives,
      dependencyRecords,
      allMaps,
    ),
    datesDataNode: buildDatesDataNode(milestoneMap, objectives, dependencyRecords, allMaps),
  };
}
